/**
 * UserTable Component
 *
 * Displays users in a sortable, filterable table using TanStack Table v8.
 * Uses standard HTML table elements (NOT MUI Table components).
 */

import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  IconButton,
  Paper,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  PersonAddOutlined as PersonAddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';

import UserStatusChip from './UserStatusChip';
import { PAGINATION_OPTIONS, DEFAULT_PAGE_SIZE, GROUP_ROLE_LABELS } from '../constants';

/**
 * ActionMenu Component - 3-dot vertical menu for row actions
 */
function ActionMenu({ user, onEdit, onDeactivate }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleClose();
    onEdit?.(user);
  };

  const handleDeactivate = () => {
    handleClose();
    onDeactivate?.(user);
  };

  return (
    <>
      <IconButton
        aria-label="Actions"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {user.status !== 'DELETED' && (
          <MenuItem onClick={handleDeactivate}>
            <ListItemIcon>
              <PersonOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

ActionMenu.propTypes = {
  user: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  onDeactivate: PropTypes.func,
};

/**
 * UserTable Component
 *
 * @param {Object} props
 * @param {Array} props.users - Array of user objects
 * @param {boolean} [props.loading] - Whether data is loading
 * @param {function} props.onEdit - Callback when edit button is clicked
 * @param {function} props.onDeactivate - Callback when deactivate button is clicked
 * @param {string} [props.externalFilter] - External search filter from parent
 */
export default function UserTable({ users, loading = false, onEdit, onDeactivate, externalFilter = '' }) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);

  // Sync external filter with internal state
  useEffect(() => {
    setGlobalFilter(externalFilter);
  }, [externalFilter]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'userName',
        header: 'Username',
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        id: 'fullName',
        header: 'Full Name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'groupRole',
        header: 'Group Role',
        cell: (info) => GROUP_ROLE_LABELS[info.getValue()] || info.getValue(),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: (info) => info.getValue() || '—',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <UserStatusChip status={info.getValue()} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <ActionMenu
            user={info.row.original}
            onEdit={onEdit}
            onDeactivate={onDeactivate}
          />
        ),
      },
    ],
    [onEdit, onDeactivate]
  );

  const table = useReactTable({
    data: users,
    columns,
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: DEFAULT_PAGE_SIZE,
      },
    },
  });

  // Loading skeleton
  if (loading) {
    return (
      <Box>
        {/* Search skeleton */}
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={40} />
        </Box>

        {/* Table skeleton */}
        <Paper sx={{ p: 2 }}>
          {[...Array(5)].map((_, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" height={60} />
            </Box>
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Table */}
      <Paper sx={{ overflowX: 'auto' }}>
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}
          role="table"
          aria-label="Users table"
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} role="row">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    role="columnheader"
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                    aria-sort={
                      header.column.getIsSorted()
                        ? header.column.getIsSorted() === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <ArrowUpIcon fontSize="small" aria-label="Sorted ascending" />,
                        desc: <ArrowDownIcon fontSize="small" aria-label="Sorted descending" />,
                      }[header.column.getIsSorted()] ?? null}
                    </Box>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                role="row"
                style={{
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    role="cell"
                    style={{
                      padding: '10px 12px',
                      fontSize: '0.875rem',
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <PersonAddIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary">
              No users found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {globalFilter ? 'Try adjusting your search criteria' : 'Get started by creating your first user'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} users
          </Typography>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              sx={{ fontSize: '0.8125rem' }}
            >
              {PAGINATION_OPTIONS.map((size) => (
                <MenuItem key={size} value={size} sx={{ fontSize: '0.8125rem' }}>
                  {size} rows
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            size="small"
          >
            <ArrowUpIcon />
          </IconButton>
          <IconButton
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            size="small"
          >
            <ArrowDownIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

UserTable.propTypes = {
  users: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDeactivate: PropTypes.func,
  externalFilter: PropTypes.string,
};
